import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, Spinner, EmptyState, ProgressBar, Avatar, Input, Select, usePagination, Pagination } from "@/components/UI";
import { TASK_TYPE_META } from "@/lib/constants";
import { Archive as ArchiveIcon, FolderCheck, CheckCircle2, Calendar, Search } from "lucide-react";

export default function Archive() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ search: "", type: "", assigneeId: "" });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data.filter((p) => p.status === "completed"))).catch(() => setProjects([]));
    api.get("/tasks").then((r) =>
      setTasks(r.data.filter((t) => ["internal", "konsep"].includes(t.type || (t.project_id ? "project" : "internal")) && t.status === "done"))
    ).catch(() => setTasks([]));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  const filteredTasks = (tasks || []).filter((t) => {
    const matchesSearch = !filter.search || t.title.toLowerCase().includes(filter.search.toLowerCase());
    const matchesType = !filter.type || (t.type || "internal") === filter.type;
    const matchesAssignee = !filter.assigneeId || String(t.assignee_id) === filter.assigneeId;
    return matchesSearch && matchesType && matchesAssignee;
  });

  const pg = usePagination(filteredTasks, 15);

  if (!projects || !tasks) return <Spinner />;

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="archive-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Arsip</h1>
        <p className="text-sm text-slate-400">
          Project yang sudah selesai (status Completed) dan task Internal/Konsep yang sudah Cleared.
        </p>
      </div>

      {/* Completed projects */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-slate-200">
          <FolderCheck size={18} className="text-[#F59E0B]" /> Project Selesai
        </h2>
        {projects.length === 0 ? (
          <EmptyState icon={ArchiveIcon} title="Belum ada project selesai" subtitle="Project dengan status Completed akan muncul di sini." />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <Card
                key={p.id}
                className="cursor-pointer p-5"
                onClick={() => navigate(`/projects/${p.id}`)}
                data-testid={`archive-project-card-${p.id}`}
              >
                <div className="mb-3 flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: p.color }} />
                  <h3 className="font-heading text-lg font-semibold text-slate-100">{p.name}</h3>
                </div>
                {p.description && <p className="mb-3 line-clamp-2 text-sm text-slate-400">{p.description}</p>}
                <div className="mb-2 flex justify-between text-xs">
                  <span className="text-slate-400">Progress (bobot)</span>
                  <span className="font-mono tabular-nums text-slate-400">{p.progress}%</span>
                </div>
                <ProgressBar value={p.progress} max={100} color={p.color} height={8} />
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><CheckCircle2 size={13} /> {p.done_tasks}/{p.total_tasks} task</span>
                  <span className="flex items-center gap-1 text-[#64748B]"><Calendar size={13} />
                    {p.end_date ? new Date(p.end_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Completed internal/konsep tasks */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-slate-200">
          <ArchiveIcon size={18} className="text-[#64748B]" /> Task Internal & Konsep Selesai
        </h2>

        <Card className="mb-4 p-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                value={filter.search}
                onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                placeholder="Cari task..."
                className="pl-9"
                data-testid="archive-search"
              />
            </div>
            <Select value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))} data-testid="archive-type-filter">
              <option value="">Semua Jenis</option>
              <option value="internal">Internal</option>
              <option value="konsep">Konsep</option>
            </Select>
            <Select value={filter.assigneeId} onChange={(e) => setFilter((f) => ({ ...f, assigneeId: e.target.value }))} data-testid="archive-pic-filter">
              <option value="">Semua PIC</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </Select>
            <button onClick={() => setFilter({ search: "", type: "", assigneeId: "" })} className="text-left text-sm text-slate-400 hover:text-slate-200" data-testid="archive-reset">
              Reset filter
            </button>
          </div>
        </Card>

        {filteredTasks.length === 0 ? (
          <EmptyState icon={ArchiveIcon} title="Belum ada task selesai" subtitle="Task Internal/Konsep yang sudah Cleared akan muncul di sini." />
        ) : (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 bg-[#020617] text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Task</th>
                  <th className="px-4 py-3">Jenis</th>
                  <th className="px-4 py-3">PIC</th>
                  <th className="px-4 py-3">Selesai</th>
                </tr>
              </thead>
              <tbody>
                {pg.paged.map((t) => {
                  const tm = TASK_TYPE_META[t.type] || TASK_TYPE_META.internal;
                  return (
                    <tr key={t.id} className="border-b border-white/5 last:border-0" data-testid={`archive-task-row-${t.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-200">{t.title}</p>
                        {t.description && <p className="max-w-xs truncate text-xs text-slate-500">{t.description}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-sm px-2 py-0.5 text-[11px] font-bold uppercase" style={{ color: tm.color, backgroundColor: tm.color + "15" }}>
                          {tm.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar src={t.assignee?.avatar_url} name={t.assignee?.name || "?"} size={22} />
                          <span className="text-xs text-slate-400">{t.assignee?.name || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        {t.completed_at ? new Date(t.completed_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} perPage={pg.perPage} onChange={pg.setPage} />
          </Card>
        )}
      </div>
    </div>
  );
}
